import React, {useCallback, useMemo} from "react"
import classNames from "classnames"
import {useQueryState} from "use-location-state"
import {useAccessToken} from "../../services/auth"
import {AsyncResultComponent} from "../../containers/AsyncResult/AsyncResult"
import {usePaging} from "../../hooks/usePaging"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {DataTableActionHeader, DataTableCard, TablePending} from "../DataTable/DataTable"
import {Filter} from "../Filter/Filter"
import {OrgFilter} from "../OrgFilter/OrgFilter"
import {findOrgs} from "../../resources/org"
import {
	AllowedUnifiedApplicationsColumns,
	findUnifiedApplications,
	UnifiedApplicationCustomerType,
} from "../../resources/unifiedApplication"
import UnifiedApplicationsTable from "./UnifiedApplicationsTable"

interface UnifiedApplicationsProps {
	limit?: number
	includedColumns: Array<AllowedUnifiedApplicationsColumns>
	enableTitle?: boolean
	fullHeight?: boolean
	refreshToken: number
	showTabsOnHeader?: boolean
}

const customerTypeOptions = new Map<UnifiedApplicationCustomerType, string>([
	[UnifiedApplicationCustomerType.Business, "Business"],
	[UnifiedApplicationCustomerType.SoleProprietor, "Sole proprietor"],
])

export function UnifiedApplications({
	limit = 25,
	includedColumns,
	fullHeight,
	enableTitle,
	refreshToken,
	showTabsOnHeader,
}: UnifiedApplicationsProps) {
	const accessToken = useAccessToken()
	const prefix = "unified-app"

	// The API accepts a single org and a single customer type, so both filters are single-select.
	const [filteredOrgs, setFilteredOrgs] = useQueryState<string[]>(`${prefix}-filter[orgId]`, [])
	const [customerTypes, setCustomerTypes] = useQueryState<UnifiedApplicationCustomerType[]>(
		`${prefix}-filter[customerType]`,
		[]
	)
	const orgId = filteredOrgs[0]
	const customerType = customerTypes[0]

	const [result, hasPrev, hasNext, prev, next, hasResults, reset] = usePaging(
		limit,
		(offset, limit) => findUnifiedApplications(accessToken, offset, limit, orgId, customerType),
		(x) => x.data.length,
		[orgId, customerType, refreshToken],
		prefix,
		true
	)

	const selectOrgs = useCallback(
		(orgIds: string[]) => {
			setFilteredOrgs(orgIds)
			reset(limit)
		},
		[setFilteredOrgs, reset, limit]
	)
	const selectCustomerTypes = useCallback(
		(types: UnifiedApplicationCustomerType[]) => {
			setCustomerTypes(types)
			reset(limit)
		},
		[setCustomerTypes, reset, limit]
	)

	const orgs = useAsyncResult(() => findOrgs(accessToken, 0, 10000), [])

	const orgNames = useMemo(
		() =>
			orgs.match(
				() => new Map<string, string>(),
				(orgs) => new Map(orgs.map((org) => [org.id, org.attributes.name])),
				() => new Map<string, string>()
			),
		[orgs]
	)

	const orgFilter = useMemo(
		() => OrgFilter(orgs, selectOrgs, filteredOrgs, reset, limit, true),
		[orgs, selectOrgs, filteredOrgs.join(","), reset, limit]
	)

	const customerTypeFilter = (
		<Filter
			title="Type"
			statuses={customerTypes}
			setStatuses={selectCustomerTypes}
			options={customerTypeOptions}
			singleSelect
		/>
	)

	return (
		<DataTableCard className={"applications-card"}>
			<DataTableActionHeader
				enableSticky={fullHeight}
				filters={[orgFilter, customerTypeFilter]}
				title={enableTitle ? "Unified Applications" : null}
				className={classNames(showTabsOnHeader && "with-title-bar-tabs")}
			/>
			<AsyncResultComponent
				asyncResult={result}
				pendingComponent={<TablePending numberOfRows={includedColumns.length} />}
			>
				{({value: unifiedApplications}) => (
					<UnifiedApplicationsTable
						unifiedApplications={unifiedApplications.data}
						orgNames={orgNames}
						hasResults={hasResults}
						hasPrev={hasPrev}
						hasNext={hasNext}
						prev={prev}
						next={next}
						includedColumns={includedColumns}
						fullHeight={fullHeight}
						showTabsOnHeader={showTabsOnHeader}
						meta={unifiedApplications.meta}
					/>
				)}
			</AsyncResultComponent>
		</DataTableCard>
	)
}
